import { dioptre, toNumber } from '../dioptre.js';
export class TranspositionError extends Error {
}
/**
 * Rotate an axis by 90 degrees, staying inside 1..180.
 *
 * Exported because the rotation is worth naming: an off-by-one here rotates a cylinder onto the
 * wrong meridian, which is a lens that is optically correct and unwearable.
 */
export function rotateAxis(axis) {
    if (!Number.isInteger(axis) || axis < 1 || axis > 180) {
        throw new TranspositionError(`Axis is an integer 1 to 180, got ${axis}`);
    }
    return axis <= 90 ? axis + 90 : axis - 90;
}
/**
 * Transpose one eye between plus-cylinder and minus-cylinder notation.
 *
 * Its own inverse: transposing twice returns the original, which is the property that makes it safe
 * to offer as a display toggle.
 *
 * A spherical prescription (zero cylinder) transposes to itself and keeps its absent axis, since
 * Appendix I rejects an axis where the cylinder is zero.
 */
export function transpose(eye) {
    const cylinder = toNumber(eye.cylinder);
    if (cylinder === 0) {
        // Nothing to rotate, and no axis to rotate. Returned as-is rather than fabricating an axis.
        return { sphere: eye.sphere, cylinder: eye.cylinder };
    }
    if (eye.axis === undefined) {
        throw new TranspositionError('Cannot transpose a cylinder with no axis. Appendix I requires an axis where the cylinder is non-zero.');
    }
    return {
        sphere: dioptre(toNumber(eye.sphere) + cylinder),
        cylinder: dioptre(-cylinder),
        axis: rotateAxis(eye.axis),
    };
}
/** Which notation an eye is written in. A zero cylinder is neither, and says so. */
export function cylinderForm(eye) {
    const cylinder = toNumber(eye.cylinder);
    if (cylinder === 0)
        return 'spherical';
    return cylinder > 0 ? 'plus' : 'minus';
}
/**
 * The canonical form `RX-003` asks to be stored alongside the entered one.
 *
 * Minus-cylinder is the canon here. The choice matters less than making one, since comparison,
 * price-band matching (Appendix V) and lab transmission all need two prescriptions written the same
 * way before they can be said to agree.
 */
export function toCanonical(eye) {
    return cylinderForm(eye) === 'plus' ? transpose(eye) : eye;
}
/** Whether two eyes are the same prescription, whichever notation each is written in. */
export function isSamePrescription(a, b) {
    const x = toCanonical(a);
    const y = toCanonical(b);
    return x.sphere === y.sphere && x.cylinder === y.cylinder && x.axis === y.axis;
}
//# sourceMappingURL=transposition.js.map